import { SolutionPage } from "@/components/shared/SolutionPage";
import { AIRoadmapMockup } from "@/components/shared/AIRoadmapMockup";
import {
  Compass,
  Map,
  Wrench,
  Rocket,
  Network,
  Search,
  Layers,
  Target,
  Gauge,
  Check,
  AlertCircle,
  Activity,
  Database,
  Brain,
} from "lucide-react";
import { Link } from "react-router-dom";
import { ServiceFAQ } from "@/components/shared/ServiceFAQ";

const AIStrategy = () => {
  return (
    <SolutionPage
      breadcrumbTitle="AI Strategy & Rollout"
      seo={{
        title: "AI Strategy, Implementation & Rollout | Frayze",
        description:
          "Frayze meets with your team, audits your goals, tools, and workflows, then builds a phased AI roadmap and implements it. Strategy before software.",
        keywords:
          "AI strategy, AI implementation, AI roadmap, AI consulting, AI adoption, AI rollout, business AI plan",
        canonical: "https://frayze.ca/solutions/ai-strategy-rollout/",
      }}
      hero={{
        badge: "The Strategy Layer: Start Here",
        badgeColor: "text-violet-600",
        title: "AI Strategy, Built Before You Build",
        description:
          "Most businesses buy AI tools before they have a plan and end up with disconnected software that no one uses. We start with a working session: understand your goals, audit your tools and workflows, then hand you a phased roadmap and build it with you.",
        buttonColor: "bg-violet-600 hover:bg-violet-700",
        gradientParams: "from-violet-500/10 via-background to-background",
        primaryCta: { text: "Book a Strategy Session", link: "/book-consultation/?tier=strategy" },
        secondaryCta: { text: "Compare Plans", link: "/pricing/" },
        visual: <AIRoadmapMockup />,
      }}
      offRamp={{
        message:
          "If you already know exactly what you want automated, you can jump straight to the build layers.",
        ctaText: "Explore Growth Systems",
        ctaLink: "/solutions/",
        accentColor: "text-violet-600",
      }}
      problem={{
        title: "Most AI Projects Fail Before They Start",
        description: (
          <>
            It&rsquo;s rarely the technology that fails. It&rsquo;s starting without a plan, buying
            tools that don&rsquo;t talk to each other, automating the wrong step, and losing the team
            the moment rollout gets hard.
            <br />
            <br />
            <strong>Strategy first means you only build what actually moves the business.</strong>
          </>
        ),
        gridCols: 3,
        cards: [
          { value: "~80%", label: "Of AI initiatives stall or never reach production without a clear roadmap." },
          { value: "Months", label: "Lost to tools that were bought before the workflow was mapped." },
          { value: "$0", label: "ROI from AI that the team never adopted or trusted." },
        ],
      }}
      systemModel={{
        title: "How a Frayze AI Engagement Runs",
        description: "A clear, phased path from first conversation to a system your team actually uses.",
        variant: "steps",
        connectionColor: "bg-gradient-to-r from-violet-500/20 via-violet-500/50 to-violet-500/20",
        items: [
          {
            step: "1",
            title: "Discover",
            description:
              "We meet with your team to understand goals, constraints, and the tools and workflows you already run.",
          },
          {
            step: "2",
            title: "Blueprint",
            description:
              "We map where AI fits, prioritise by impact vs. effort, and deliver a phased roadmap with clear ROI.",
          },
          {
            step: "3",
            title: "Build",
            description:
              "We implement and develop the systems (integrations, automations, and custom AI) in priority order.",
          },
          {
            step: "4",
            title: "Rollout",
            description:
              "We launch in stages, train your people, and measure adoption so the system sticks and improves.",
          },
        ],
      }}
      layers={{
        title: "What You Actually Get",
        description:
          "Not a slide deck that sits in a drawer. A working plan, the build to back it, and a team that knows how to use it.",
        theme: "indigo",
        items: [
          {
            title: "Discovery & Goal Mapping",
            icon: Compass,
            items: ["Stakeholder working sessions", "Business goals & success metrics", "Constraints & risk review"],
          },
          {
            title: "Tools & Workflow Audit",
            icon: Search,
            items: ["Current stack inventory", "Data & integration map", "Manual bottleneck analysis"],
          },
          {
            title: "Phased AI Roadmap",
            icon: Map,
            items: ["Prioritised use cases", "Impact vs. effort scoring", "Timeline, budget & ROI model"],
          },
          {
            title: "Implementation & Development",
            icon: Wrench,
            items: ["Integrations & automations", "Custom AI agents & workflows", "Connected to your CRM"],
          },
          {
            title: "Rollout & Enablement",
            icon: Rocket,
            items: ["Staged go-live", "Team training & SOPs", "Change management support"],
          },
          {
            title: "Measurement & Iteration",
            icon: Gauge,
            items: ["Adoption tracking", "Performance reporting", "Continuous optimisation"],
          },
        ],
      }}
      interdependence={{
        title: "The Layer That Makes Every Other Layer Work",
        description: (
          <>
            Strategy isn&rsquo;t separate from the build. It&rsquo;s what gives the build direction. The
            roadmap we create here decides what gets installed across your{" "}
            <Link to="/solutions/crm-automation/" className="font-bold underline decoration-violet-500/50 hover:text-violet-400 transition-colors">
              CRM foundation
            </Link>
            ,{" "}
            <Link to="/solutions/lead-generation-system/" className="font-bold underline decoration-violet-500/50 hover:text-violet-400 transition-colors">
              marketing system
            </Link>
            , and{" "}
            <Link to="/platform/" className="font-bold underline decoration-violet-500/50 hover:text-violet-400 transition-colors">
              custom AI automation
            </Link>
            {" "}and in what order. When the roadmap calls for voice, the AI answers on the{" "}
            <Link to="/platform/business-phone-system/" className="font-bold underline decoration-violet-500/50 hover:text-violet-400 transition-colors">
              business phone system
            </Link>{" "}
            itself (recorded, logged, and worked from the CRM).
          </>
        ),
        icon: Network,
        quote: "Architecture before tactics. Strategy before software.",
      }}
      useCases={{
        title: "Who This Is For",
        items: [
          {
            title: "AI-Curious, Plan-Less",
            description: "You know AI matters but don't know where to start or what's worth doing first.",
            link: "/solutions/",
          },
          {
            title: "Tool Sprawl",
            description: "You've bought software that doesn't connect, and adoption has stalled.",
            link: "/solutions/crm-automation/",
          },
          {
            title: "Scaling Operations",
            description: "Growth is straining your team and you need a deliberate automation plan, including enterprise AI.",
            link: "/platform/",
          },
        ],
      }}
      socialProof={{
        title: "Phased Plans, Real Outcomes",
        quote:
          '"Frayze didn\'t hand us a tool. They handed us a plan, then built it phase by phase. Each stage paid for the next."',
        icon: Target,
        ctaLink: "/case-studies/need-a-car-automotive-growth/",
        ctaText: "Read a Phased Rollout Case Study",
      }}
      cta={{
        title: "Start With a Plan, Not a Purchase",
        description:
          "Book a working session. We'll map your goals, audit your tools and workflows, and show you exactly where AI earns its keep and in what order to build it.",
        primaryCta: { text: "Book Your AI Strategy Session", link: "/book-consultation/?tier=strategy" },
        secondaryCta: { text: "View Pricing", link: "/pricing/" },
        color: "bg-violet-600 hover:bg-violet-700 text-white",
      }}
      customSection={
        <div className="space-y-24 pb-24">
          {/* What you walk away with */}
          <section className="container mx-auto px-6 max-w-4xl">
            <div className="reveal text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">What You Walk Away With</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Every engagement starts with a fixed-fee strategy sprint. The fee is credited toward your
                build if you move forward, so the plan pays for itself.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
              {[
                "A documented map of your current tools, data, and workflows",
                "A prioritised AI roadmap scored by impact vs. effort",
                "A phased timeline with budget and projected ROI",
                "A clear first build (scoped, costed, and ready to start)",
              ].map((item, i) => (
                <div key={item} className={`reveal lift-hover flex items-start gap-3 p-5 rounded-xl border bg-muted/20 ${i % 2 === 1 ? "reveal-delay-1" : ""}`}>
                  <Check className="w-5 h-5 text-violet-600 shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-foreground/90">{item}</span>
                </div>
              ))}
            </div>
          </section>

          {/* When the roadmap calls for custom builds */}
          <section className="container mx-auto px-6 max-w-4xl">
            <div className="reveal text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">When the Roadmap Calls for Custom AI Automation</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                As your business scales, some workflows demand purpose-built AI agents that operate 24/7. The strategy phase determines whether and when to build them.
              </p>
            </div>

            <div className="space-y-8 mb-12">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="reveal lift-hover flex gap-4 p-6 rounded-xl border bg-muted/20">
                  <Activity className="w-6 h-6 text-rose-600 shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold mb-2">Autonomous Lead Response</h3>
                    <p className="text-sm text-muted-foreground">AI that answers calls, qualifies leads, and books appointments so nothing falls through the cracks.</p>
                  </div>
                </div>
                <div className="reveal reveal-delay-1 lift-hover flex gap-4 p-6 rounded-xl border bg-muted/20">
                  <Brain className="w-6 h-6 text-rose-600 shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold mb-2">Intelligent Routing & Decisions</h3>
                    <p className="text-sm text-muted-foreground">Custom workflows that route to the right team, automate approvals, and handle exceptions without human delay.</p>
                  </div>
                </div>
                <div className="reveal lift-hover flex gap-4 p-6 rounded-xl border bg-muted/20">
                  <Database className="w-6 h-6 text-rose-600 shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold mb-2">Platform Integration</h3>
                    <p className="text-sm text-muted-foreground">AI connected directly to your ERP, CRM, POS, calendars, and custom APIs (one data layer that works across systems).</p>
                  </div>
                </div>
                <div className="reveal reveal-delay-1 lift-hover flex gap-4 p-6 rounded-xl border bg-muted/20">
                  <AlertCircle className="w-6 h-6 text-rose-600 shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold mb-2">Operational Intelligence</h3>
                    <p className="text-sm text-muted-foreground">Real-time sentiment analysis, adoption tracking, and performance scoring so you see what's working.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="reveal bg-rose-50 dark:bg-rose-900/10 border border-rose-100 dark:border-rose-900/20 rounded-xl p-8">
              <h3 className="font-bold mb-4 text-lg">Built for Complex Operations at Scale</h3>
              <p className="text-muted-foreground mb-6">
                Enterprise AI makes sense when your roadmap shows you need to handle high-volume lead response, multi-location workflows, or sophisticated routing logic. We build these systems to integrate seamlessly with your existing tools (no rip-and-replace).
              </p>
              <div className="space-y-2">
                <p className="text-sm font-semibold">Typical platforms we integrate with:</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm text-muted-foreground">
                  {["ERP Systems", "Salesforce & HubSpot", "Google & Outlook", "Custom APIs", "Shopify & Magento", "Data Warehouses"].map((platform) => (
                    <div key={platform} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-rose-600" /> {platform}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Where it sits in the system */}
          <section className="reveal bg-violet-50 dark:bg-violet-900/10 border border-violet-100 dark:border-violet-900/20 rounded-xl p-8 max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 text-violet-700 dark:text-violet-400 mb-4">
              <Layers className="w-5 h-5" />
              <span className="font-bold uppercase tracking-wider text-xs">The Front Door</span>
            </div>
            <h2 className="text-xl font-bold mb-4">This Is Layer Zero</h2>
            <p className="text-muted-foreground mb-6">
              Frayze builds growth as connected layers: CRM, marketing, visibility, scale, and enterprise AI.
              This engagement is the layer that comes first: it decides which of those you need, and when.
            </p>
            <Link
              to="/solutions/"
              className="text-sm font-bold text-violet-700 dark:text-violet-400 hover:underline inline-flex items-center gap-1"
            >
              See the full system →
            </Link>
            <p className="mt-6 text-sm text-muted-foreground">
              Federal funding now covers AI adoption.{" "}
              <Link
                to="/canada-ai-strategy-small-business/"
                className="font-semibold text-violet-700 dark:text-violet-400 hover:underline"
              >
                see if you qualify
              </Link>
              .
            </p>
          </section>

          <ServiceFAQ
            title="AI Strategy & Rollout FAQ"
            description="The questions teams ask before committing to a plan."
            items={[
              {
                question: "Do I have to buy the whole roadmap up front?",
                answer:
                  "No. The roadmap is phased on purpose. We start with a fixed-fee strategy sprint, then you build one phase at a time. Each one is scoped and costed before it starts, so you stay in control of spend.",
              },
              {
                question: "What does the strategy engagement actually cost?",
                answer:
                  "It's a fixed-fee sprint sized to your business, and the fee is credited toward your first build phase if you move forward. We'll quote it on the first call once we understand scope. See /pricing/ for the build tiers it feeds into.",
              },
              {
                question: "We already have some tools. Do we have to replace them?",
                answer:
                  "Almost never. Part of the audit is mapping what you already run. Where your tools are working, we integrate around them. We only recommend replacing something when it's actively blocking the outcome.",
              },
              {
                question: "How long does it take?",
                answer:
                  "The discovery-to-roadmap sprint typically takes 1–3 weeks depending on the size of your team and stack. Implementation timelines depend on the phases you choose and they're laid out clearly in the roadmap.",
              },
              {
                question: "What if my team won't actually use it?",
                answer:
                  "That's exactly why rollout and enablement are built into the engagement, not bolted on after. We launch in stages, train your people on real workflows, and track adoption so the system sticks.",
              },
              {
                question: "What about custom AI automation: when does that fit in?",
                answer:
                  "The strategy phase determines whether your roadmap calls for custom AI builds (lead response, workflow automation, operational intelligence). If it does, we scope and cost that work in the roadmap phase, then implement it on the timeline that makes sense for your business.",
              },
              {
                question: "Is custom AI secure, and can it handle our data?",
                answer:
                  "Yes. Custom AI instances are isolated and your data is not used to train public models. We adhere to strict privacy standards and can deploy in dedicated Canadian or US data centers for compliance requirements.",
              },
              {
                question: "What happens if the AI doesn't know the answer?",
                answer:
                  "For operational AI systems, we configure a 'human handoff' protocol. If confidence drops below a threshold, the system automatically flags a human agent to take over seamlessly so no leads or decisions fall through the cracks.",
              },
            ]}
          />
        </div>
      }
    />
  );
};

export default AIStrategy;
